import React from "react";
import { api } from "./utils/api";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { ArticleViewer } from "./ArticleViewer";
import { useReadAloud, ReadableItem } from "./utils/useReadAloud";
import { ReadAloudBar } from "./utils/ReadAloudBar";

type Order = "asc" | "desc";
type SortBy = "premarket_change_rel" | "regular_hours_change_rel";

interface InterceptedItem {
  id: string;
  url: string;

  title: string; // article title
  source: string; // eg bloomberg, reuters
  tickers: string[]; // all tickers mentioned in the article

  published_at: number; // unix timestamp in seconds
  formatted_published: string; // formatted date in NYC time
}

interface SimulateItem {
  ticker: string;
  last_price: number;
  change_abs: number;
  change_rel: number;
  regular_hours_last: number;
  regular_hours_change_abs: number;
  regular_hours_change_rel: number;
  premarket_active: boolean;
  premarket_last: number;
  premarket_change_abs: number;
  premarket_change_rel: number;
  intercepted: InterceptedItem[];
}

interface SimulateGroup {
  sector: string;
  items: SimulateItem[];
  avg_change_rel: number;
  premarket_change_rel: number;
}

function todayNYC(): string {
  return new Date().toLocaleDateString("en-CA", {
    timeZone: "America/New_York",
  });
}

// dateStr in "YYYY-MM-DD" format -> "Mon", "Tue", etc.
function dayOfWeekAbbrev(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
}

// Key format: ra__TICKER__INTERCEPTID
function makeReadKey(ticker: string, interceptId: string) {
  return `ra__${ticker}__${interceptId}`;
}
function tickerFromReadKey(key: string): string {
  const parts = key.split("__");
  return parts.length >= 3 && parts[0] === "ra" ? parts[1] : "";
}

export const SimulateWatchlist = (props?: any) => {
  const [targetDate, setTargetDate] = React.useState<string>(
    () =>
      props?.params?.targetDate ||
      localStorage.getItem("simulateWatchlistDate") ||
      todayNYC(),
  );
  const [groups, setGroups] = React.useState<SimulateGroup[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [order, setOrder] = React.useState<Order>("desc");
  const [sortBy, setSortBy] = React.useState<SortBy>("premarket_change_rel");
  const [daysBack, setDaysBack] = React.useState<number>(3);
  const [tickerFilter, setTickerFilter] = React.useState("");
  const [expandedIntercepted, setExpandedIntercepted] = React.useState<
    Set<string>
  >(new Set());
  const [onlyWithIntercepts, setOnlyWithIntercepts] = React.useState(true);
  const [enabledSources, setEnabledSources] = React.useState<
    Record<"bloomberg" | "reuters", boolean>
  >({ bloomberg: true, reuters: true });
  const [activeArticleUrl, setActiveArticleUrl] = React.useState<string>("");
  const [maxTickers, setMaxTickers] = React.useState<string>("");
  const [marked, setMarked] = React.useState<
    { ticker: string; date: string }[]
  >([]);
  const [readingTickerIndex, setReadingTickerIndex] = React.useState(0);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const savedScrollTop = React.useRef(0);
  const prevReadingTickerRef = React.useRef<string>("");
  const allFilteredItemsRef = React.useRef<SimulateItem[]>([]);

  const {
    voiceControls,
    setVoiceControls,
    currentlyReadingKey,
    toggleReading,
    skipItem,
  } = useReadAloud();

  // Opened with a target date (e.g. from Reddit Analysis' "Sim. watchlist" button) -> fetch immediately
  React.useEffect(() => {
    if (props?.params?.targetDate) doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!containerRef.current) return;
    if (activeArticleUrl) {
      savedScrollTop.current = containerRef.current.scrollTop;
      containerRef.current.scrollTop = 0;
    } else {
      containerRef.current.scrollTop = savedScrollTop.current;
    }
  }, [activeArticleUrl]);

  // Reset ticker index when new data arrives
  React.useEffect(() => {
    setReadingTickerIndex(0);
    prevReadingTickerRef.current = "";
  }, [groups]);

  // Drive expand, scroll-to-ticker, and ticker-index display from reading progress
  React.useEffect(() => {
    if (!currentlyReadingKey) return;

    const ticker = tickerFromReadKey(currentlyReadingKey);
    if (!ticker) return;

    // Expand intercepts section for this ticker
    setExpandedIntercepted((prev) => {
      if (prev.has(ticker)) return prev;
      const next = new Set(prev);
      next.add(ticker);
      return next;
    });

    // Update the displayed ticker index
    const idx = allFilteredItemsRef.current.findIndex(
      (i) => i.ticker === ticker,
    );
    if (idx >= 0) setReadingTickerIndex(idx);

    // On ticker change: scroll ticker row into view, then article after expand renders
    if (ticker !== prevReadingTickerRef.current) {
      prevReadingTickerRef.current = ticker;
      document
        .getElementById(`ticker-row-${ticker}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // Defer article scroll so expand has time to render
    const t = setTimeout(() => {
      document
        .getElementById(currentlyReadingKey)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 120);
    return () => clearTimeout(t);
  }, [currentlyReadingKey]);

  function toggleIntercepted(ticker: string) {
    setExpandedIntercepted((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  }

  function doFetch() {
    if (!targetDate) return;
    localStorage.setItem("simulateWatchlistDate", targetDate);
    setError("");
    setIsLoading(true);
    setGroups([]);
    api
      .get(
        `/simulate/watchlist?target_date=${targetDate}&order=${order}&sort_by=${sortBy}&days_back=${daysBack}`,
      )
      .then((res) => setGroups(res.data))
      .catch((err) =>
        setError(
          err.response?.data?.detail || "Error fetching simulation data.",
        ),
      )
      .finally(() => setIsLoading(false));
  }

  function openChart(ticker: string) {
    if (!props?.containerApi) return;
    const endDate = new Date(targetDate);
    endDate.setDate(endDate.getDate() - 1);
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - 2);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    addFloatingPanel(props.containerApi, "realtime_chart", {
      ticker,
      startDate: fmt(startDate),
      endDate: fmt(endDate),
      interval: "60",
    });
  }

  const tf = tickerFilter.trim().toLowerCase();

  function passesSourceFilter(intercept: InterceptedItem): boolean {
    return enabledSources[intercept.source as "bloomberg" | "reuters"] ?? true;
  }

  // Build flat filtered item list (mirrors render filter logic)
  const allFilteredItems: SimulateItem[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (tf.length >= 2 && !item.ticker.toLowerCase().startsWith(tf)) continue;
      if (
        onlyWithIntercepts &&
        !item.intercepted.some(
          (i) =>
            passesSourceFilter(i) &&
            (maxTickers === "" || i.tickers.length <= parseInt(maxTickers)),
        )
      )
        continue;
      allFilteredItems.push(item);
    }
  }
  allFilteredItemsRef.current = allFilteredItems;

  // Build flat ReadableItem list and ticker start-index map
  const readItems: ReadableItem[] = [];
  const tickerStartIndex: number[] = [];
  let riIdx = 0;
  for (const item of allFilteredItems) {
    tickerStartIndex.push(riIdx);
    const intercepts = item.intercepted
      .filter(
        (i) =>
          passesSourceFilter(i) &&
          (maxTickers === "" || i.tickers.length <= parseInt(maxTickers)),
      )
      .sort((a, b) => b.published_at - a.published_at);
    for (const intercept of intercepts) {
      readItems.push({
        key: makeReadKey(item.ticker, intercept.id),
        texts: [intercept.title],
      });
      riIdx++;
    }
  }

  const currentReadingTicker = tickerFromReadKey(currentlyReadingKey);

  // Items passed to ReadAloudBar start from the selected ticker
  const safeTickerIndex = Math.min(
    readingTickerIndex,
    Math.max(0, allFilteredItems.length - 1),
  );
  const startIdx = tickerStartIndex[safeTickerIndex] ?? 0;
  const activeReadItems = readItems.slice(startIdx);

  return (
    <div className="flex flex-col h-full">
      <div ref={containerRef} className="p-4 flex-1 overflow-auto">
        {error && <div className="text-red-500 mb-4">{error}</div>}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            doFetch();
          }}
          className="flex flex-col sm:flex-row gap-6 mb-4 items-start"
        >
          <div className="flex flex-col gap-2">
            <label className="font-semibold">Date</label>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="YYYY-MM-DD"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="border rounded px-2 py-1 w-36 font-mono"
              />


              <button type="submit" className="btn btn-primary h-9">
                Fetch
              </button>
            </div>
          </div>

              <div className="flex flex-col gap-2 w-16">
                <label htmlFor="sw-days" className="font-semibold">Days:</label>
                <input
                  id="sw-days"
                  type="number"
                  min={1}
                  max={30}
                  value={daysBack}
                  onChange={(e) =>
                    setDaysBack(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="border rounded px-2 py-1 w-20 font-mono"
                />
              </div>

          <div className="flex flex-col">
            <span className="font-semibold mb-1">Order</span>
            {(["desc", "asc"] as Order[]).map((v) => (
              <label key={v} className="flex items-center gap-1">
                <input
                  type="radio"
                  name="order"
                  value={v}
                  checked={order === v}
                  onChange={() => setOrder(v)}
                />
                {v}
              </label>
            ))}
          </div>

          <div className="flex flex-col">
            <span className="font-semibold mb-1">Sort by</span>
            {(
              ["premarket_change_rel", "regular_hours_change_rel"] as SortBy[]
            ).map((v) => (
              <label key={v} className="flex items-center gap-1">
                <input
                  type="radio"
                  name="sort_by"
                  value={v}
                  checked={sortBy === v}
                  onChange={() => setSortBy(v)}
                />
                {v === "premarket_change_rel"
                  ? "Pre-market %"
                  : "Regular hours %"}
              </label>
            ))}
          </div>
        </form>

        <div className="flex items-center gap-4 mb-4">
          <input
            type="text"
            placeholder="Search ticker..."
            value={tickerFilter}
            onChange={(e) => setTickerFilter(e.target.value)}
            className="border rounded px-2 py-1 w-60"
          />
          <label className="flex items-center gap-1 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={onlyWithIntercepts}
              onChange={(e) => setOnlyWithIntercepts(e.target.checked)}
            />
            Only with intercepts
          </label>
          <div className="flex items-center gap-3">
            {(["bloomberg", "reuters"] as const).map((source) => (
              <label
                key={source}
                className="flex items-center gap-1 cursor-pointer text-sm capitalize"
              >
                <input
                  type="checkbox"
                  checked={enabledSources[source]}
                  onChange={(e) =>
                    setEnabledSources((prev) => ({
                      ...prev,
                      [source]: e.target.checked,
                    }))
                  }
                />
                {source}
              </label>
            ))}
          </div>
          <form onSubmit={(e) => e.preventDefault()} className="flex items-center gap-2">
            <label className="text-sm font-semibold">Max tickers:</label>
            <input
              type="number"
              min={1}
              placeholder="any"
              value={maxTickers}
              onChange={(e) => setMaxTickers(e.target.value)}
              className="border rounded px-2 py-1 w-20 font-mono"
            />
          </form>
        </div>

        {/* Reading ticker index control */}
        {allFilteredItems.length > 0 && (
          <div className="flex items-center gap-2 mb-3 text-sm w-[300px] text-nowrap">
            <span className="font-semibold text-gray-600">Reading:</span>
            <input
              type="number"
              min={1}
              max={allFilteredItems.length}
              value={safeTickerIndex + 1}
              onChange={(e) => {
                const v = parseInt(e.target.value) - 1;
                if (!isNaN(v) && v >= 0 && v < allFilteredItems.length)
                  setReadingTickerIndex(v);
              }}
              className="border rounded px-2 py-1 w-16 font-mono"
            />
            <span className="text-gray-500">/ {allFilteredItems.length}</span>
            {currentReadingTicker && (
              <span className="font-bold text-yellow-700">
                {currentReadingTicker}
              </span>
            )}
          </div>
        )}

        {isLoading && <div>Loading...</div>}

        {!isLoading && groups.length > 0 && groups[0].items.length === 0 && (
          <div className="text-gray-500">No trading data for {targetDate}.</div>
        )}

        {groups.map((group) => {
          const filtered = group.items.filter(
            (item) =>
              (tf.length < 2 || item.ticker.toLowerCase().startsWith(tf)) &&
              (!onlyWithIntercepts ||
                item.intercepted.some(
                  (i) =>
                    passesSourceFilter(i) &&
                    (maxTickers === "" || i.tickers.length <= parseInt(maxTickers)),
                )),
          );

          if (filtered.length === 0) return null;

          return (
            <div
              key={group.sector}
              className="border-r-2 border-l-2 block sm:inline-block sm:mr-4 mt-4 w-[600px]"
            >
              <div className="bg-slate-300 p-1 flex justify-between">
                <span>
                  {targetDate}{" "}
                  {targetDate && `(${dayOfWeekAbbrev(targetDate)})`}
                </span>
                <span>
                  {group.avg_change_rel.toFixed(2)}% (pre:{" "}
                  {group.premarket_change_rel.toFixed(2)}%)
                </span>
              </div>

              <div className="grid grid-cols-[auto_auto_auto_auto_auto] gap-x-4">
                {filtered.map((item) => {
                  const isMatch =
                    tf.length >= 2 && item.ticker.toLowerCase().startsWith(tf);
                  const isReadingTicker = currentReadingTicker === item.ticker;
                  const filteredIntercepts = item.intercepted.filter(
                    (intercept) =>
                      passesSourceFilter(intercept) &&
                      (maxTickers === "" || intercept.tickers.length <= parseInt(maxTickers)),
                  );
                  return (
                    <div
                      key={item.ticker}
                      id={`ticker-row-${item.ticker}`}
                      className={`col-span-5 grid grid-cols-subgrid ${isMatch || isReadingTicker ? "bg-yellow-200" : ""}`}
                    >
                      <a
                        className="px-1 pt-2 cursor-pointer"
                        onClick={() => openChart(item.ticker)}
                      >
                        {item.ticker}
                      </a>
                      <span className="px-1 pt-2 text-right ">
                        {item.last_price.toFixed(2)}
                      </span>
                      <span className="px-1 pt-2 text-right">
                        {item.change_abs.toFixed(2)}
                      </span>
                      <span className="px-1 pt-2 text-right">
                        {item.change_rel.toFixed(2)}%
                      </span>
                      <button
                        type="button"
                        className={`px-1 pt-2 text-sm text-right rounded ${marked.some((m) => m.ticker === item.ticker && m.date === targetDate) ? "bg-red-200" : "text-gray-400"}`}
                        onClick={() =>
                          setMarked((prev) =>
                            prev.some(
                              (m) =>
                                m.ticker === item.ticker && m.date === targetDate,
                            )
                              ? prev.filter(
                                  (m) =>
                                    !(
                                      m.ticker === item.ticker &&
                                      m.date === targetDate
                                    ),
                                )
                              : [
                                  ...prev,
                                  { ticker: item.ticker, date: targetDate },
                                ],
                          )
                        }
                      >
                        {marked.some(
                          (m) =>
                            m.ticker === item.ticker && m.date === targetDate,
                        )
                          ? "Unmark"
                          : "Mark"}
                      </button>

                      <span className="px-1 pb-1 text-gray-400">pre:</span>
                      <span className="px-1 pb-1 text-right text-gray-400">
                        {item.premarket_last.toFixed(2)}
                      </span>
                      <span className="px-1 pb-1 text-right text-gray-400">
                        {item.premarket_change_abs.toFixed(2)}
                      </span>
                      <span className="px-1 pb-1 text-right text-gray-400">
                        {item.premarket_change_rel.toFixed(2)}%
                      </span>
                      <a
                        onClick={() => toggleIntercepted(item.ticker)}
                        className={`cursor-pointer px-1 pb-1 text-sm text-right ${filteredIntercepts.length === 0 ? "text-gray-400" : "font-bold"}`}
                      >
                        {filteredIntercepts.length} intercepted{" "}
                        {expandedIntercepted.has(item.ticker) ? "▲" : "▼"}
                      </a>

                      {expandedIntercepted.has(item.ticker) && (
                        <div className="col-span-5 px-1 pb-1 w-0 min-w-full">
                          <p className="font-semibold break-words">
                            Intercepted:
                          </p>
                          {filteredIntercepts.length > 0 ? (
                            [...filteredIntercepts]
                              .sort((a, b) => b.published_at - a.published_at).map((intercept) => {
                              const readKey = makeReadKey(item.ticker, intercept.id);
                              const isReadingArticle = currentlyReadingKey === readKey;
                              return (
                                <div
                                  key={intercept.id}
                                  id={readKey}
                                  className={`flex items-start gap-2 ${isReadingArticle ? "bg-yellow-100" : ""}`}
                                >
                                  <div className="break-words whitespace-pre-wrap">
                                    [{intercept.formatted_published}] [{intercept.source}] [{" "}{intercept.tickers.join(", ")}] {intercept.title}
                                    <div className="flex gap-2 mt-1">
                                      <a
                                        href={intercept.url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        (source)
                                      </a>

                                      {intercept.url && (
                                        <a
                                          href="/"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setActiveArticleUrl(intercept.url);
                                          }}
                                        >
                                          (text)
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-gray-400">No intercepted items</p>
                          )}
                        </div>
                      )}

                      <div className="col-span-5 border-b border-gray-300" />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {activeArticleUrl !== "" && (
          <ArticleViewer
            url={activeArticleUrl}
            setActiveArticleUrl={setActiveArticleUrl}
          />
        )}
      </div>

      <ReadAloudBar
        voiceControls={voiceControls}
        setVoiceControls={setVoiceControls}
        toggleReading={toggleReading}
        skipItem={skipItem}
        items={activeReadItems}
        containerApi={props?.containerApi}
        panelApi={props?.api}
      />
    </div>
  );
};
